import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { fetchPageEntities } from '../../services/drawingGeometryApi';
import {
  deriveSheetScale,
  formatFeetInches,
  formatViewportReadout,
  pdfPointsToRealFeet,
} from '../../utils/drawingMeasurements.js';
import {
  clearScaleOverride,
  parseCalibrationInput,
  resolveEffectiveSheetScale,
  saveScaleOverride,
  scaleOverrideKey,
} from '../../utils/sheetScaleStore.js';
import {
  CUSTOM_SCALE_PRESET_ID,
  findPresetByInput,
  SCALE_PRESET_GROUPS,
} from '../../utils/drawingScalePresets.js';

const Section = styled.div`
  flex-shrink: 0;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
`;

const Header = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 10px 16px;
  border: none;
  background: rgba(0, 0, 0, 0.02);
  font-size: 12px;
  font-weight: 700;
  color: #444;
  cursor: pointer;
`;

const Body = styled.div`
  padding: 10px 16px 14px;
  font-size: 12px;
  color: #4b5563;
  line-height: 1.55;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 6px;

  &:first-child {
    margin-top: 0;
  }
`;

const Key = styled.span`
  color: #6b7280;
  flex-shrink: 0;
`;

const Val = styled.span`
  font-weight: 600;
  color: #1f2937;
  text-align: right;
`;

const Hint = styled.div`
  margin-top: 8px;
  font-size: 11px;
  color: #9ca3af;
  line-height: 1.45;
`;

const Select = styled.select`
  width: 100%;
  height: 32px;
  margin-top: 6px;
  padding: 0 8px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 12px;
  background: #fff;
`;

const ALL_SCALE_PRESETS = SCALE_PRESET_GROUPS.flatMap((g) => g.presets);

const Input = styled.input`
  width: 100%;
  height: 32px;
  margin-top: 6px;
  padding: 0 8px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 12px;
`;

const BtnRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
`;

const SmallBtn = styled.button`
  flex: 1;
  border: none;
  border-radius: 6px;
  padding: 7px 8px;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  background: ${(p) => (p.$primary ? '#e97024' : '#f3f4f6')};
  color: ${(p) => (p.$primary ? '#fff' : '#374151')};
`;

export default function ScaleReadoutPanel({
  activeSheet,
  activePage,
  entityPages,
  viewportInfo,
  projectId,
  requirementId,
  scaleOverride,
  onScaleOverrideChange,
}) {
  const [open, setOpen] = useState(true);
  const [pageEntities, setPageEntities] = useState([]);
  const [titleEntities, setTitleEntities] = useState([]);
  const [calibrationInput, setCalibrationInput] = useState('');
  const [selectedPresetId, setSelectedPresetId] = useState(CUSTOM_SCALE_PRESET_ID);

  const syncPresetFromInput = useCallback((text) => {
    const preset = findPresetByInput(text);
    setSelectedPresetId(preset?.id || CUSTOM_SCALE_PRESET_ID);
  }, []);

  const ir = activeSheet?.ir;
  const pageIndex = Math.max(0, (activePage || 1) - 1);

  useEffect(() => {
    if (!entityPages?.length || !ir) {
      setPageEntities([]);
      setTitleEntities([]);
      return undefined;
    }

    let cancelled = false;
    const loadShard = async (idx) => {
      const shard = entityPages.find((p) => p.pageIndex === idx);
      if (!shard?.url) return [];
      try {
        return await fetchPageEntities(shard);
      } catch {
        return [];
      }
    };

    (async () => {
      const [current, title] = await Promise.all([
        loadShard(pageIndex),
        pageIndex === 0 ? [] : loadShard(0),
      ]);
      if (cancelled) return;
      setPageEntities(current);
      setTitleEntities(title);
    })();

    return () => { cancelled = true; };
  }, [entityPages, ir, activePage, pageIndex]);

  const detectedScale = useMemo(() => {
    if (!ir?.components?.length) return null;
    const mergedEntities = [...pageEntities, ...titleEntities];
    return deriveSheetScale(ir.components, mergedEntities, pageIndex)
      || deriveSheetScale(ir.components, mergedEntities, null)
      || deriveSheetScale(ir.components, [], null);
  }, [ir?.components, pageEntities, titleEntities, pageIndex]);

  const sheetScale = useMemo(
    () => resolveEffectiveSheetScale(detectedScale, scaleOverride),
    [detectedScale, scaleOverride],
  );

  const sheetId = activeSheet?.sheetId || '';

  useEffect(() => {
    const label = scaleOverride?.label
      || detectedScale?.ratio?.label
      || '';
    setCalibrationInput(label);
    syncPresetFromInput(label);
  }, [scaleOverride?.label, detectedScale?.ratio?.label, sheetId, syncPresetFromInput]);

  const handlePresetChange = (e) => {
    const presetId = e.target.value;
    setSelectedPresetId(presetId);
    if (presetId === CUSTOM_SCALE_PRESET_ID) return;
    const preset = ALL_SCALE_PRESETS.find((p) => p.id === presetId);
    if (!preset?.input) return;
    setCalibrationInput(preset.input);
    const ratio = parseCalibrationInput(preset.input);
    if (!ratio) return;
    const key = scaleOverrideKey(projectId, requirementId, sheetId);
    const payload = { ratio, label: preset.label };
    saveScaleOverride(key, ratio, { label: preset.label });
    onScaleOverrideChange?.(payload);
  };

  const applyCalibration = () => {
    const ratio = parseCalibrationInput(calibrationInput);
    if (!ratio) return;
    const key = scaleOverrideKey(projectId, requirementId, sheetId);
    const payload = { ratio, label: ratio.label };
    saveScaleOverride(key, ratio, { label: ratio.label });
    onScaleOverrideChange?.(payload);
  };

  const clearCalibration = () => {
    const key = scaleOverrideKey(projectId, requirementId, sheetId);
    clearScaleOverride(key);
    onScaleOverrideChange?.(null);
    const label = detectedScale?.ratio?.label || '';
    setCalibrationInput(label);
    syncPresetFromInput(label);
  };

  const viewportLine = formatViewportReadout({
    pageNum: viewportInfo?.pageNum,
    numPages: viewportInfo?.numPages,
    zoomPercent: viewportInfo?.zoomPercent,
    pageSize: ir?.pageSize,
  });

  if (!activeSheet) {
    return null;
  }

  return (
    <Section>
      <Header type="button" onClick={() => setOpen((v) => !v)}>
        <span>Scale & viewport</span>
        <span>{open ? '▾' : '▸'}</span>
      </Header>
      {open && (
        <Body>
          <Row>
            <Key>Viewport</Key>
            <Val>{viewportLine}</Val>
          </Row>
          <Row>
            <Key>Sheet scale</Key>
            <Val>
              {sheetScale?.ratio
                ? sheetScale.ratio.label
                : (sheetScale?.label || 'Not detected')}
            </Val>
          </Row>
          {sheetScale?.ratio && sheetScale.barLengthPdfPoints > 0 && (
            <Row>
              <Key>Bar length</Key>
              <Val>
                {formatFeetInches(
                  (sheetScale.barLengthPdfPoints * (sheetScale.ratio.denominator / sheetScale.ratio.numerator)) / 72 / 12,
                  { precision: 0 },
                )}
                {' '}
                <span style={{ fontWeight: 400, color: '#9ca3af' }}>
                  ({Math.round(sheetScale.barLengthPdfPoints)} pt)
                </span>
              </Val>
            </Row>
          )}
          {sheetScale?.source && (
            <Row>
              <Key>Source</Key>
              <Val>
                {sheetScale.source.replace(/_/g, ' ')}
                {' · page '}
                {(sheetScale.pageIndex ?? 0) + 1}
              </Val>
            </Row>
          )}
          {sheetScale?.ratio && (
            <Row>
              <Key>Real / PDF pt</Key>
              <Val>
                {formatFeetInches(pdfPointsToRealFeet(1, sheetScale.ratio), { precision: 2 })}
              </Val>
            </Row>
          )}
          {scaleOverride?.ratio && (
            <Row>
              <Key>Override</Key>
              <Val style={{ color: '#c45f12' }}>User calibrated</Val>
            </Row>
          )}
          <div style={{ marginTop: 12 }}>
            <Key style={{ display: 'block', marginBottom: 4 }}>Calibrate scale</Key>
            <Select
              value={selectedPresetId}
              onChange={handlePresetChange}
              aria-label="Typical drawing scales"
            >
              {SCALE_PRESET_GROUPS.map((group) => (
                <optgroup key={group.id} label={group.label}>
                  {group.presets.map((preset) => (
                    <option key={preset.id} value={preset.id}>
                      {preset.label}
                    </option>
                  ))}
                </optgroup>
              ))}
              <option value={CUSTOM_SCALE_PRESET_ID}>Custom…</option>
            </Select>
            {(selectedPresetId === CUSTOM_SCALE_PRESET_ID || !findPresetByInput(calibrationInput)) && (
              <Input
                value={calibrationInput}
                onChange={(e) => {
                  setCalibrationInput(e.target.value);
                  setSelectedPresetId(CUSTOM_SCALE_PRESET_ID);
                }}
                placeholder="e.g. 1:48 or 1/8 in = 1 ft"
              />
            )}
            <BtnRow>
              <SmallBtn type="button" $primary onClick={applyCalibration}>Apply</SmallBtn>
              <SmallBtn type="button" onClick={clearCalibration}>Clear</SmallBtn>
            </BtnRow>
          </div>
          {!sheetScale?.ratio && (
            <Hint>
              Scale is read from title-block notation (e.g. 1/8&quot; = 1&apos;-0&quot;, 1:100) or bar-scale labels. NTS sheets show no calibrated scale.
            </Hint>
          )}
        </Body>
      )}
    </Section>
  );
}
